import { Snowflake } from "lucide-react";
import { Button } from "@/components/ui/button";

const PRIVACY_URL = "https://sprayfoaminsulation-crm.online/privacy";
const TERMS_URL = "https://sprayfoaminsulation-crm.online/terms";

const FEATURES = [
  {
    title: "Plan and generate content.",
    body: "Turn a topic and a service area into ready-to-review drafts for Facebook, Instagram, X and Threads, written for the neighborhoods you actually serve.",
  },
  {
    title: "Publish and schedule.",
    body: "Connect your own business accounts and schedule posts. Every post carries a tracked link back to your quote form, so you know which content produced which lead.",
  },
  {
    title: "Capture and route leads.",
    body: "Quote requests land in a shared list with the campaign, the territory and the salesperson already attached.",
  },
  {
    title: "See what search is doing.",
    body: "Read your own Google Search Console queries and your Google Analytics traffic next to the content you published, and decide what to write next.",
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#09090B] text-white" data-testid="public-landing-page">
      <div className="max-w-[1100px] mx-auto px-6">
        <nav className="flex items-center justify-between py-6" data-testid="landing-nav">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-[#0055FF] grid place-items-center">
              <Snowflake className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">SprayOps</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-[#A1A1AA]">
            <a href={PRIVACY_URL} className="hover:text-white transition-colors hidden sm:inline" data-testid="landing-nav-privacy-link">
              Privacy
            </a>
            <a href={TERMS_URL} className="hover:text-white transition-colors hidden sm:inline" data-testid="landing-nav-terms-link">
              Terms
            </a>
            <a href="/login" data-testid="landing-nav-sign-in-link">
              <Button size="sm" className="bg-white text-[#09090B] hover:bg-[#E4E4E7] rounded-md font-medium">
                Sign in
              </Button>
            </a>
          </div>
        </nav>

        <section className="py-16 sm:py-24 border-t border-[#27272A]">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl">
            SprayOps
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-[#D4D4D8] max-w-2xl">
            Content and social media operations for local service contractors
          </p>
          <p className="mt-6 text-base text-[#A1A1AA] max-w-2xl leading-relaxed">
            SprayOps is a private workspace where a contracting business plans its marketing content, publishes it
            to its own social accounts, captures the leads that content generates, and reads the search performance
            of its own website — all in one place.
          </p>
          <a href="/login" data-testid="landing-hero-sign-in-link">
            <Button className="mt-8 h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC] rounded-md font-medium active:scale-[0.98] transition-colors">
              Sign in
            </Button>
          </a>
        </section>

        <section className="py-16 border-t border-[#27272A]">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">What you can do</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-8">
            {FEATURES.map((f) => (
              <div key={f.title}>
                <h3 className="font-heading font-bold text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-[#A1A1AA] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-16 border-t border-[#27272A]" data-testid="landing-google-data-section">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">How SprayOps uses your Google data</h2>
          <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed max-w-2xl">
            SprayOps requests read-only access to two Google services, and only after you explicitly connect them
            from the Connections screen:
          </p>

          <div className="mt-6 space-y-6">
            <div>
              <h3 className="font-heading font-bold text-white">
                Google Search Console{" "}
                <span className="text-[#71717A] font-normal text-sm break-all">
                  — https://www.googleapis.com/auth/webmasters.readonly
                </span>
              </h3>
              <p className="mt-2 text-sm text-[#A1A1AA] leading-relaxed">
                We read the search queries, clicks, impressions and average position of the websites you already
                own and have verified in Search Console. We show them on the Keywords screen so you can see which
                search terms already bring visitors to your site and choose the topics to cover next.
              </p>
            </div>
            <div>
              <h3 className="font-heading font-bold text-white">
                Google Analytics{" "}
                <span className="text-[#71717A] font-normal text-sm break-all">
                  — https://www.googleapis.com/auth/analytics.readonly
                </span>
              </h3>
              <p className="mt-2 text-sm text-[#A1A1AA] leading-relaxed">
                We read aggregate sessions, traffic sources, top pages and conversions for the Analytics property
                you select. We show them on the Analytics screen so you can see whether the content you published
                is actually moving traffic and leads.
              </p>
            </div>
          </div>

          <p className="mt-6 text-sm text-[#A1A1AA] leading-relaxed max-w-2xl">
            We never request write access. We never modify anything in your Google account. We do not sell this
            data, do not use it for advertising, and do not share it with anyone. You can disconnect at any time
            from the Connections screen inside SprayOps, or directly from your Google Account at{" "}
            <a
              href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer"
              className="text-[#0055FF] hover:underline"
            >
              https://myaccount.google.com/permissions
            </a>
            . The full detail is in our{" "}
            <a href={PRIVACY_URL} className="text-[#0055FF] hover:underline" data-testid="landing-body-privacy-link">
              Privacy Policy
            </a>
            .
          </p>
        </section>

        <footer className="py-10 border-t border-[#27272A] text-sm text-[#71717A]" data-testid="landing-footer">
          SprayOps is operated by Olimpo Web Design LLC.{" "}
          <a href={PRIVACY_URL} className="text-[#A1A1AA] hover:text-white" data-testid="landing-footer-privacy-link">
            Privacy Policy
          </a>
          {" · "}
          <a href={TERMS_URL} className="text-[#A1A1AA] hover:text-white" data-testid="landing-footer-terms-link">
            Terms of Service
          </a>
          {" · "}contacto@olimpo.tech{" · "}
          <a href="https://olimpo.tech" target="_blank" rel="noreferrer" className="text-[#A1A1AA] hover:text-white">
            olimpo.tech
          </a>
        </footer>
      </div>
    </div>
  );
}
